import React from 'react';
import DocumentRow from './DocumentRow';
import { DOCUMENT_STATES } from '../../lib/compliance';

// A labelled group of document rows on /portal/compliance. Headings
// follow the doc-state colour code from the brief.
//
// EMPTY GROUPS COLLAPSE (tidy-up §1). They used to render a heading, a count
// badge and an italic paragraph while holding nothing, so a supplier with
// everything in order — the common case, and the one this page should read
// best for — spent roughly the first third of the screen on Expiring Soon (0)
// and Awaiting Review (0). The summary card's legend already reports both
// counts, so nothing is lost by omitting them.
//
// The teaching copy survives where it earns its place: a supplier who has
// never submitted anything gets the empty groups explained, because for them
// the structure IS the information. `teachEmpty` says which case we're in.

const GROUP_STYLES = {
  [DOCUMENT_STATES.OUTSTANDING]: {
    heading: 'Outstanding',
    colour: '#E0290A',
    emptyText: null, // never empty by definition – if it is, omit entirely
  },
  [DOCUMENT_STATES.EXPIRED]: {
    heading: 'Expired',
    colour: '#8E1B06',
    emptyText: null,
  },
  [DOCUMENT_STATES.NEEDS_CLARIFICATION]: {
    heading: 'Needs Clarification',
    colour: '#E89A2E',
    // Only renders when there's at least one item. An empty section
    // would read as "Fusion may ask for clarification later" which
    // is true but noise; we surface this group only when it matters.
    emptyText: null,
  },
  [DOCUMENT_STATES.EXPIRING_SOON]: {
    heading: 'Expiring Soon',
    colour: '#E89A2E',
    emptyText: 'Approved documents with less than 30 days until expiry will appear here.',
  },
  [DOCUMENT_STATES.AWAITING_REVIEW]: {
    heading: 'Awaiting Review',
    colour: '#2E8FA1',
    emptyText: "Documents you've uploaded will appear here while Fusion 2K is reviewing them.",
  },
  [DOCUMENT_STATES.APPROVED]: {
    heading: 'Approved',
    colour: '#92D400',
    emptyText: 'Documents that Fusion 2K has reviewed and approved will appear here.',
  },
};

export default function DocumentGroup({ state, entries, teachEmpty = false, onUpload, onView, onReply, onRespond, onAcknowledge, onCompleteForm, onSignPdf, onDelegate, delegationsByKey, onOpenHistory, messagesByKey }) {
  const style = GROUP_STYLES[state];
  if (!style) return null;
  const isEmpty = !entries || entries.length === 0;
  // Outstanding / Expired groups are only shown when there's content –
  // an empty Outstanding group would say "Empty" which is misleading
  // when the supplier has nothing to upload.
  //
  // And since §1, the same now goes for the three groups that DO carry
  // teaching copy — unless this is a supplier with nothing submitted at all,
  // for whom the empty structure is the explanation.
  if (isEmpty && (!style.emptyText || !teachEmpty)) return null;

  return (
    <div className="mb-7">
      <div
        className="flex items-center gap-2.5 font-['Metropolis'] text-[11px] font-extrabold uppercase tracking-[0.2em] mb-3"
        style={{ color: style.colour }}
      >
        <span>{style.heading}</span>
        <span className="bg-[#EEF1E6] border border-[#DDE1D0] text-[#51534A] px-1.5 py-px rounded text-[10px] font-extrabold tabular-nums">
          {entries?.length || 0}
        </span>
      </div>
      {isEmpty ? (
        <div className="font-['Metropolis'] text-[12px] text-[#51534A] italic py-2">
          {style.emptyText}
        </div>
      ) : (
        // ONE container per group, with the rows divided inside it (§7).
        // Each row used to be its own bordered, shadowed, rounded card with its
        // own margin — seven of those is a lot of scroll for uniform rows.
        // Rows carrying a conversation opt out and keep the card treatment;
        // they sit outside this container's border by virtue of their own.
        <div className="bg-white border border-[#E6EADB] rounded-xl overflow-hidden">
        {entries.map(entry => (
          <DocumentRow
            key={entry.meta.key}
            entry={entry}
            onUpload={onUpload}
            onView={onView}
            onReply={onReply}
            onRespond={onRespond}
            onAcknowledge={onAcknowledge}
            onCompleteForm={onCompleteForm}
            onSignPdf={onSignPdf}
            onDelegate={onDelegate}
            delegation={(delegationsByKey || {})[entry.meta.key] || null}
            onOpenHistory={onOpenHistory}
            messages={messagesByKey?.[entry.meta.key]}
          />
        ))}
        </div>
      )}
    </div>
  );
}
